import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@insurancemenu/imenu-components-lib';
import { ArrowBack } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import { ModalType } from 'global/utils/ModalsHelper';
import ActionModal from 'components/common/modals/ActionModal';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { CHECKLIST_HEADER_BACK_BTN_TEST_ID } from 'components/common/rfq/constants';
import { createStyles } from './styles';
import { EXIT_WITHOUT_SAVING_MODAL_TEST_ID, SAVING_BEFORE_EXIT_MODAL_TEST_ID } from '../constants';

interface Props {
  /**
   * Header Title
   */
  title: string;
  /**
   * Header Subtitle
   */
  subtitle?: string | JSX.Element;
  /**
   * Header custom styles
   */
  className?: string;
  /**
   * Form completion status used to open the warning modal if 'showWarningModals' is set to true.
   */
  isFormCompleted?: boolean;
  /**
   * Flag which defines whether to show warning modals before exiting.
   * It is used for pages with forms to notify the user that data may be lost without saving.
   */
  showWarningModals?: boolean;
  /**
   * Custom back button title. 'Back' is by default
   */
  backButtonTitle?: string;
  /**
   * Save changes callback if showWarningModals is set to true
   */
  onSaveChangesAction?: () => Promise<void>;
  /**
   * Custom redirect back callback
   */
  onRedirectBack?: () => void;
  'data-test-id'?: string;
}

const RFQChecklistOptionHeader = observer((props: Props): JSX.Element => {
  const classes = createStyles();
  const {
    showWarningModals = false,
    title,
    subtitle,
    isFormCompleted,
    backButtonTitle,
    className = '',
    onSaveChangesAction,
    onRedirectBack,
  } = props;

  const {
    exitModalStore: { showExitModal, openModal, closeModal },
  } = useRootStore();

  const history = useHistory();
  // Component actions

  function onHistoryGoBack(): void {
    history.goBack();
  }

  function onModalCloseAndRedirectBack(): void {
    closeModal();
    if (onRedirectBack) {
      onRedirectBack();
    } else {
      onHistoryGoBack();
    }
  }

  async function onModalCloseAndSave(): Promise<void> {
    // If save changes callback isn't provided, on action close the modal
    if (onSaveChangesAction) {
      await onSaveChangesAction();
      closeModal();
    } else {
      closeModal();
    }
  }

  const backButtonStyle = !subtitle && classes.backButtonInLine;

  return (
    <>
      <header className={clsx(classes.header, className)} data-test-id={props['data-test-id']}>
        <Button
          leftIconProp={<ArrowBack />}
          type="text"
          size="medium"
          onClick={showWarningModals ? openModal : onModalCloseAndRedirectBack}
          className={clsx(classes.backButton, backButtonStyle)}
          data-test-id={CHECKLIST_HEADER_BACK_BTN_TEST_ID}
        >
          {backButtonTitle || 'Back'}
        </Button>
        <h1 className={classes.title}>{title}</h1>
        {subtitle && <div className={classes.caption}>{subtitle}</div>}
      </header>
      {showWarningModals && (
        <>
          {isFormCompleted ? (
            <ActionModal
              show={showExitModal}
              title="Would you like to exit without saving your changes?"
              modalType={ModalType.NOTIFICATION}
              closable={false}
              closeButtonText="Exit Without Saving"
              actionButtonText="Stay on this page"
              onClose={onModalCloseAndRedirectBack}
              onAction={closeModal}
              data-test-id={EXIT_WITHOUT_SAVING_MODAL_TEST_ID}
            />
          ) : (
            <ActionModal
              show={showExitModal}
              title="Would you like to save your changes before exiting?"
              modalType={ModalType.NOTIFICATION}
              closable={false}
              closeButtonText="Exit Without Saving"
              actionButtonText="Save Changes"
              onClose={onModalCloseAndRedirectBack}
              onAction={onModalCloseAndSave}
              data-test-id={SAVING_BEFORE_EXIT_MODAL_TEST_ID}
            />
          )}
        </>
      )}
    </>
  );
});

export default RFQChecklistOptionHeader;
